import { motion } from "framer-motion";
import { Target, Globe2, Users, Compass } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import MetricsBand from "@/components/MetricsBand";
import CTASection from "@/components/CTASection";

const VALUES = [
  { icon: Target, title: "Outcome-led", desc: "We measure success in pipeline, revenue, and payback — not activity." },
  { icon: Users, title: "Operator DNA", desc: "Senior practitioners who've built and scaled revenue across geos." },
  { icon: Compass, title: "Strategic clarity", desc: "Sharp ICPs, sharp messaging, and a clear sequence of bets." },
  { icon: Globe2, title: "Global by design", desc: "Native pods across US, EMEA, and APAC with in-market fluency." },
];

const IMG_TEAM = "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="bg-white">
        <div className="sozos-container pt-12 md:pt-16 pb-20 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <p className="eyebrow">About Sozos</p>
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#0A1F44] mt-4 tracking-tight leading-[1.02]"
              >
                We build the revenue engines<br /> ambitious companies wish they had years ago.
              </motion.h1>
              <p className="text-lg md:text-xl text-slate-600 mt-7 leading-relaxed max-w-2xl">
                Sozos Global Services partners with founders, CROs, and CMOs to unlock qualified pipeline
                at scale. Our model blends elite human teams with AI revenue agents to deliver outcomes
                in-house teams typically can&apos;t — at a fraction of the cost.
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              <img src={IMG_TEAM} alt="Sozos team collaborating" className="w-full h-[420px] object-cover rounded-sm border border-slate-200" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="sozos-container section-pad grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow">Our mission</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#0A1F44] mt-3 tracking-tight leading-tight">
              Make world-class revenue accessible to every ambitious company.
            </h2>
          </div>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Most companies don&apos;t have a product problem. They have a distribution problem.
            Sozos exists to close that gap — giving every team the SDRs, strategy, marketing,
            RevOps, and AI agents they need to win their market. We embed into your team,
            move at the speed of a startup, and operate with the rigor of a top-tier consulting firm.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="sozos-container section-pad">
          <SectionHeader eyebrow="What we believe" title="The principles behind every engagement." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`about-value-${i}`}
                className="bg-white border border-slate-200 rounded-md p-7 card-hover"
              >
                <div className="w-11 h-11 bg-[#0A1F44] text-white rounded-sm flex items-center justify-center">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-[#0A1F44] mt-5 tracking-tight">{v.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <MetricsBand />
      <CTASection title="Let's build your next chapter of growth." subtitle="Partner with Sozos" />
    </div>
  );
}
